import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Modal, Slider, Switch, Button, Tabs, Tooltip, Space, Alert } from 'antd';
import Icon from '@components/Icon';
import stores from '@stores';

// 滤镜预设方案（名称将在组件内通过翻译获取）
const filterPresetsConfig = [
    { 
        nameKey: 'filter.presetNames.original', 
        icon: <Icon.ImagePlay size={18} />,
        filter: {
            blur: 0,
            brightness: 100,
            contrast: 100,
            grayscale: 0,
            hueRotate: 0,
            saturate: 100,
            sepia: 0,
            invert: 0,
        }
    },
    { 
        nameKey: 'filter.presetNames.blackWhite', 
        icon: <Icon.FileText size={18} />,
        filter: {
            grayscale: 100,
            contrast: 120,
            brightness: 110,
        }
    },
    { 
        nameKey: 'filter.presetNames.vintage', 
        icon: <Icon.RotateCcw size={18} />,
        filter: {
            sepia: 70,
            brightness: 90,
            contrast: 110,
        }
    },
    { 
        nameKey: 'filter.presetNames.fresh', 
        icon: <Icon.Sun size={18} />,
        filter: {
            brightness: 110,
            saturate: 120,
            contrast: 110,
            hueRotate: 10,
        }
    },
    { 
        nameKey: 'filter.presetNames.warm', 
        icon: <Icon.Sun size={18} />,
        filter: {
            saturate: 130,
            hueRotate: 15,
            brightness: 105,
        }
    },
    { 
        nameKey: 'filter.presetNames.cool', 
        icon: <Icon.Moon size={18} />,
        filter: {
            saturate: 110,
            hueRotate: -15,
            brightness: 100,
        }
    },
    { 
        nameKey: 'filter.presetNames.invert', 
        icon: <Icon.FlipVertical2 size={18} />,
        filter: {
            invert: 100,
            brightness: 90,
            contrast: 120,
        }
    },
    { 
        nameKey: 'filter.presetNames.soft', 
        icon: <Icon.Circle size={18} />,
        filter: {
            blur: 1,
            brightness: 105,
            saturate: 90,
        }
    }
];

// 滤镜控制项（名称将在组件内通过翻译获取）
const filterControlsConfig = [
    { key: 'blur', nameKey: 'filter.controls.blur', min: 0, max: 10, step: 0.1, unit: 'px', icon: <Icon.Circle size={16} /> },
    { key: 'brightness', nameKey: 'filter.controls.brightness', min: 0, max: 200, step: 1, unit: '%', icon: <Icon.Sun size={16} /> },
    { key: 'contrast', nameKey: 'filter.controls.contrast', min: 0, max: 200, step: 1, unit: '%', icon: <Icon.Moon size={16} /> },
    { key: 'grayscale', nameKey: 'filter.controls.grayscale', min: 0, max: 100, step: 1, unit: '%', icon: <Icon.Square size={16} /> },
    { key: 'hueRotate', nameKey: 'filter.controls.hueRotate', min: -180, max: 180, step: 1, unit: 'deg', icon: <Icon.Palette size={16} /> },
    { key: 'saturate', nameKey: 'filter.controls.saturate', min: 0, max: 200, step: 1, unit: '%', icon: <Icon.Settings2 size={16} /> },
    { key: 'sepia', nameKey: 'filter.controls.sepia', min: 0, max: 100, step: 1, unit: '%', icon: <Icon.RotateCcw size={16} /> },
    { key: 'invert', nameKey: 'filter.controls.invert', min: 0, max: 100, step: 1, unit: '%', icon: <Icon.FlipVertical2 size={16} /> },
];

const ImageFilter = observer(() => {
    const { t } = useTranslation('toolbar');
    const [open, setOpen] = useState(false);
    const [filterView, setFilterView] = useState('presets'); // 'presets' 或 'custom'
    
    // 当点击快捷工具中的滤镜按钮时，打开滤镜弹窗
    const handleOpenModal = () => {
        setOpen(true);
        
        // 如果之前未启用过滤镜，则打开时自动启用
        if (!stores.option.enableFilter) {
            stores.option.toggleFilter(true);
        }
    };
    
    // 应用预设滤镜
    const applyPreset = (presetConfig) => {
        // 应用预设前先重置所有滤镜参数
        stores.option.resetFilter();
        
        // 然后应用预设的参数
        Object.keys(presetConfig.filter).forEach(key => {
            stores.option.setFilter(key, presetConfig.filter[key]);
        });
        
        // 确保滤镜是启用的
        stores.option.toggleFilter(true);
    };
    
    // 重置滤镜为默认值
    const resetFilters = () => {
        stores.option.resetFilter();
    };
    
    return (
        <>
            <Tooltip title={t('filter.tooltip')}>
                <Button
                    type="default"
                    className="flex flex-col items-center justify-center h-auto py-2 px-3 w-[70px]"
                    onClick={handleOpenModal}
                >
                    <Icon.Settings2 size={20} useFa={true} className="mb-1 text-fuchsia-500 dark:text-fuchsia-400" />
                    <span className="text-xs">{t('filter.button')}</span>
                </Button>
            </Tooltip>
            
            <Modal
                title={
                    <div className="flex items-center text-purple-600 dark:text-purple-400">
                        <Icon.Settings2 size={20} className="mr-2" />
                        <span>{t('filter.title')}</span>
                    </div>
                }
                open={open}
                onCancel={() => setOpen(false)}
                footer={null}
                width={700}
                bodyStyle={{ padding: "20px", maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
            >
                <Alert 
                    message={t('filter.previewMode')} 
                    description={t('filter.previewDescription')} 
                    type="info" 
                    showIcon 
                    className="mb-4"
                />
                
                <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center">
                        <Switch 
                            checked={stores.option.enableFilter} 
                            onChange={(checked) => stores.option.toggleFilter(checked)}
                            className="mr-2" 
                        />
                        <span className="text-sm font-medium">
                            {stores.option.enableFilter ? t('filter.enabled') : t('filter.disabled')}
                        </span>
                    </div>
                    <Button 
                        onClick={resetFilters}
                        icon={<Icon.RotateCcw size={16} className="mr-1" />}
                    >
                        {t('filter.reset')}
                    </Button>
                </div>
                
                <Tabs
                    activeKey={filterView}
                    onChange={setFilterView}
                    items={[
                        {
                            key: 'presets',
                            label: t('filter.presets'),
                            children: (
                                <div className="grid grid-cols-4 gap-4">
                                    {filterPresetsConfig.map((presetConfig, index) => (
                                        <div 
                                            key={index}
                                            className="flex flex-col items-center cursor-pointer p-4 border rounded-lg hover:shadow-md hover:border-purple-300 transition-all"
                                            onClick={() => applyPreset(presetConfig)}
                                        >
                                            <div className="mb-2 text-purple-500">{presetConfig.icon}</div>
                                            <span className="text-sm">{t(presetConfig.nameKey)}</span>
                                        </div>
                                    ))}
                                </div>
                            ),
                        },
                        {
                            key: 'custom',
                            label: t('filter.custom'),
                            children: (
                                <div className="space-y-4">
                                    {filterControlsConfig.map((control) => (
                                        <div key={control.key} className="mb-2">
                                            <div className="flex items-center mb-1">
                                                <div className="text-purple-500 mr-1">{control.icon}</div>
                                                <span className="text-sm font-medium">{t(control.nameKey)}</span>
                                                <span className="ml-auto text-xs">
                                                    {stores.option.filter[control.key]}{control.unit}
                                                </span>
                                            </div>
                                            <Slider
                                                min={control.min}
                                                max={control.max}
                                                step={control.step}
                                                value={stores.option.filter[control.key]}
                                                onChange={(value) => stores.option.setFilter(control.key, value)}
                                                className="mb-4"
                                            />
                                        </div>
                                    ))}
                                </div>
                            ),
                        },
                    ]}
                />
                
                <div className="mt-4 border-t pt-4">
                    <div className="text-sm font-medium mb-2">{t('filter.preview')}</div>
                    <div 
                        className="w-full h-48 bg-cover bg-center rounded-lg shadow-inner border overflow-hidden"
                        style={{ 
                            backgroundImage: `url(${stores.editor.img?.src})`,
                            filter: stores.option.enableFilter ? stores.option.filterString : 'none'
                        }}
                    />
                </div>
            </Modal>
        </>
    );
});

export default ImageFilter; 